import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import useModalClose from 'hooks/useModalClose'
import { Button, CursorPointer, Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Card, CardBody } from 'components/Card/Card'
import { IKitchenDish } from 'types/kitchen/kitchenDish.interface'
import { IKitchenMenuDay, IKitchenMenuTemplate } from 'types/kitchen/kitchenMenu.interface'
import { IKitchenSite } from 'types/kitchen/kitchenSite.interface'

interface IProps {
  templates: { [id: number]: IKitchenMenuTemplate }
  tplSel: number
  setTplSel: (id: number) => void
  tplStart: string
  setTplStart: (d: string) => void
  fills: { key: string; dishId: number; day: string; w: string }[]
  skipped: number
  kitchen: IKitchenSite
  days: IKitchenMenuDay[]
  dishes: IKitchenDish[]
  closeModal: () => void
  onApply: () => void
}

export const InstantiateTemplateModal = ({
  templates,
  tplSel,
  setTplSel,
  tplStart,
  setTplStart,
  fills,
  skipped,
  kitchen,
  days,
  dishes,
  closeModal,
  onApply,
}: IProps) => {
  useModalClose(closeModal)

  const dish = (id: number) => dishes.find((d) => d.id === id)
  const templateOptions = Object.values(templates).map((t) => ({
    label: `${t.name} — ${t.length}-day cycle`,
    value: t.id,
  }))
  const futureDays = days.filter((d) => d.kind !== 'past')
  const startOptions = futureDays.map((d) => ({ label: d.label, value: d.d }))

  return (
    <Modal black width='560px'>
      <Flex column gap='1rem' width='100%'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>Instantiate Menu Cycle Template</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <HonestSelectInput
          width='100%'
          labelText='Template'
          name='template'
          options={templateOptions}
          defaultValue={tplSel}
          onChange={(v: any) => setTplSel(Number(v.value))}
        />
        <HonestSelectInput
          width='100%'
          labelText='Start date (cycle day 1)'
          name='startDate'
          options={startOptions}
          defaultValue={tplStart}
          onChange={(v: any) => setTplStart(v.value || '')}
        />
        <Card>
          <CardBody>
            <BodyText weight='700' style={{ marginBottom: '0.375rem' }}>
              Preview — {kitchen.name}, this week
            </BodyText>
            <OtherText grey>
              <BodyText weight='700' style={{ display: 'inline', color: '#24D07B' }}>
                {fills.length}
              </BodyText>
              slots will be filled as <BodyText weight='700' style={{ display: 'inline' }}>DRAFT</BodyText>
              {skipped > 0 && (
                <>
                  <BodyText weight='700' style={{ display: 'inline', color: '#ff9900' }}>
                    {skipped}
                  </BodyText>
                  skipped (published or already assigned — templates never overwrite)
                </>
              )}
            </OtherText>
            {fills.slice(0, 5).map((f) => (
              <OtherText key={f.key} grey>
                · {f.day} {f.w} → {dish(f.dishId)?.name}
              </OtherText>
            ))}
            {fills.length > 5 && <OtherText grey>… and {fills.length - 5} more</OtherText>}
          </CardBody>
        </Card>
        <OtherText grey>
          Templates carry patterns only — no dates, no quantities, no publication. Instantiated drafts are reviewed against real stock and real forecast in the grid, then published per window.
        </OtherText>
        <Flex gap='0.5rem' justcenter width='100%'>
          <Button onClick={closeModal}>Cancel</Button>
          <Button orange disabled={fills.length === 0} onClick={onApply}>
            Publish
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
